import { AxiosError } from 'axios';
import { Card } from 'components/common';
import { CommonButton as Button } from 'components/common/Forms/Button';
import {
  CloseButton,
  DialogFormWrapper,
  FormActionsStyle,
  FormBody,
  FormHeader,
  FormRow,
  FormTitle,
} from 'components/common/Forms/Forms.styled';
import { CommonInput as Input } from 'components/common/Forms/Input';
import TextArea from 'components/common/Forms/TextArea';
import { CardLoader } from 'components/common/Loading';
import { ReactElement, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FaCube, FaTimes } from 'react-icons/fa';
import { CubicleData, CubicleFormValues } from 'types/cubicles';
import { useCommonTranslation } from 'hooks/i18n/useCommonTranslation';

interface Props {
  closeFormHandler: (err?: true | AxiosError<any>) => void;
  submitHandler: (data: CubicleFormValues, isUpdateCubicle: boolean) => void;
  isLoading: boolean;
  prefilledData?: CubicleData;
  handleDeleteCubicle: (value: string) => void;
}

export const CubiclesForm = ({
  closeFormHandler,
  submitHandler,
  isLoading,
  prefilledData,
  handleDeleteCubicle,
}: Props): ReactElement => {
  const isEdit = Boolean(prefilledData);
  const { t } = useCommonTranslation();
  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors },
  } = useForm<CubicleFormValues>({
    defaultValues: prefilledData
      ? {
          ...prefilledData,
        }
      : {},
  });

  useEffect(() => {
    // This for setting focus after form mounted for better user experience
    setFocus('name');
  }, [setFocus]);

  return (
    <Card>
      <DialogFormWrapper>
        <CloseButton
          size="auto"
          variant="primary"
          iconOnly
          onClick={() => closeFormHandler()}
        >
          <FaTimes />
        </CloseButton>
        <FormHeader>
          <FaCube />
          <FormTitle>{t('cubicle')}</FormTitle>
        </FormHeader>
        <FormBody
          onSubmit={handleSubmit((data) => submitHandler(data, isEdit))}
        >
          <FormRow>
            <Input
              label={t('name')}
              placeholder={t('name')}
              helperText={errors.name?.message}
              hasError={Boolean(errors.name)}
              id={prefilledData ? prefilledData.id + '_name' : 'new_name'}
              {...register('name', {
                required: t('must-not-empty') as string,
              })}
              required
            />
          </FormRow>
          <FormRow>
            <TextArea
              label={t('description')}
              placeholder={t('description')}
              helperText={errors.description?.message}
              hasError={Boolean(errors.description)}
              id={
                prefilledData
                  ? prefilledData.id + '_description'
                  : 'new_description'
              }
              {...register('description')}
            />
          </FormRow>
          <FormActionsStyle align="center">
            <Button variant="primary" type="submit">
              {isEdit ? t('save') : t('add')}
            </Button>
            {prefilledData && (
              <Button
                variant="danger"
                type="button"
                onClick={() => handleDeleteCubicle(prefilledData.id)}
              >
                {t('delete')}
              </Button>
            )}
            <Button
              type="button"
              variant="secondary"
              onClick={() => closeFormHandler()}
            >
              {t('cancel')}
            </Button>
          </FormActionsStyle>
        </FormBody>
        {isLoading && <CardLoader fillWrapper={true} />}
      </DialogFormWrapper>
    </Card>
  );
};
